import { areBoxPropsEqual } from '#pplx/frontend/aether/primitives/BoxPrimitive/areBoxPropsEqual.js';
import { resolveBoxMarginClasses, resolveBoxPaddingClasses, resolveBoxRoundedClasses, } from '#pplx/frontend/aether/primitives/BoxPrimitive/boxResponsiveClasses.js';
import { getBoxShadowClasses, getBoxShadowOverlayClasses, } from '#pplx/frontend/aether/primitives/BoxPrimitive/internal/boxShadowClasses.js';
import { resolveResponsiveClasses } from '#pplx/frontend/aether/primitives/BoxPrimitive/resolveResponsiveClasses.js';
import classNames from 'classnames';
import React from 'react';
const BoxHoverVariant = {
    quiet: 'quiet',
    subtle: 'subtle',
};
// The divider color has zero specificity so a child's own border color, like a button outline, wins.
// The child selector mirrors Tailwind v3's `divide-*` rule (`> :not([hidden]) ~ :not([hidden])`);
// v4 switches divide utilities to `> :not(:last-child)`, so revisit this together with the upgrade.
export const boxBorderClass = 'border-soft ring-soft [:where(&>:not([hidden])~:not([hidden]))]:border-soft';
const DISPLAY_CLASSES = {
    flex: 'flex',
    block: 'block',
    'inline-block': 'inline-block',
    'inline-flex': 'inline-flex',
    grid: 'grid',
    none: 'hidden',
};
// Display class maps for registered breakpoints.
// Responsive variant classes are covered by the Tailwind safelist (see safelist.ts).
const BREAKPOINT_DISPLAY_CLASSES = {
    default: DISPLAY_CLASSES,
    sm: {
        flex: 'sm:flex',
        block: 'sm:block',
        'inline-block': 'sm:inline-block',
        'inline-flex': 'sm:inline-flex',
        grid: 'sm:grid',
        none: 'sm:hidden',
    },
    md: {
        flex: 'md:flex',
        block: 'md:block',
        'inline-block': 'md:inline-block',
        'inline-flex': 'md:inline-flex',
        grid: 'md:grid',
        none: 'md:hidden',
    },
    lg: {
        flex: 'lg:flex',
        block: 'lg:block',
        'inline-block': 'lg:inline-block',
        'inline-flex': 'lg:inline-flex',
        grid: 'lg:grid',
        none: 'lg:hidden',
    },
    xl: {
        flex: 'xl:flex',
        block: 'xl:block',
        'inline-block': 'xl:inline-block',
        'inline-flex': 'xl:inline-flex',
        grid: 'xl:grid',
        none: 'xl:hidden',
    },
    '2xl': {
        flex: '2xl:flex',
        block: '2xl:block',
        'inline-block': '2xl:inline-block',
        'inline-flex': '2xl:inline-flex',
        grid: '2xl:grid',
        none: '2xl:hidden',
    },
};
const directionClasses = {
    row: '',
    col: 'flex-col',
};
const alignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    baseline: 'items-baseline',
    stretch: 'items-stretch',
};
const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly',
};
const gapClasses = {
    '2xs': 'gap-0.5',
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    ml: 'gap-6',
    lg: 'gap-8',
    xl: 'gap-12',
};
const gapXClasses = {
    '2xs': 'gap-x-0.5',
    xs: 'gap-x-1',
    sm: 'gap-x-2',
    md: 'gap-x-4',
    ml: 'gap-x-6',
    lg: 'gap-x-8',
    xl: 'gap-x-12',
};
const gapYClasses = {
    '2xs': 'gap-y-0.5',
    xs: 'gap-y-1',
    sm: 'gap-y-2',
    md: 'gap-y-4',
    ml: 'gap-y-6',
    lg: 'gap-y-8',
    xl: 'gap-y-12',
};
function getBoxGapClassNames(gap) {
    if (!gap)
        return '';
    if (typeof gap === 'string')
        return gapClasses[gap];
    return classNames(gap.x ? gapXClasses[gap.x] : '', gap.y ? gapYClasses[gap.y] : '');
}
function getBoxDisplayClassNames({ display, direction, align, justify, gap, wrap, }) {
    const displayClass = resolveResponsiveClasses(display, DISPLAY_CLASSES, BREAKPOINT_DISPLAY_CLASSES);
    if (!displayClass)
        return '';
    return classNames(displayClass, direction ? directionClasses[direction] : '', align ? alignClasses[align] : '', justify ? justifyClasses[justify] : '', getBoxGapClassNames(gap), wrap === true ? 'flex-wrap' : wrap === 'reverse' ? 'flex-wrap-reverse' : '');
}
// Derives the hover variant from the bg prop.
// bg="subtle" → hover "quiet"; all others → hover "subtle".
function resolveInteractiveHover(bg) {
    return bg === 'subtle' ? 'quiet' : 'subtle';
}
export const BoxPrimitive = React.memo(function BoxPrimitive({ bg, className, onClick, onMouseOver, onMouseLeave, children, id, interactive, shadowSize, hoverShadow, shadowFocused = false, focusShadowOnPrimaryAction = false, shadowLayer = 'box', style, testId, as = 'div', ref, noBorder, rounded, padding, margin, display, direction, align, justify, gap, wrap, ...rest }) {
    const boxClass = React.useMemo(() => {
        // Define variant classes hash map
        const variantClasses = {
            super: 'bg-super-strong',
            quiet: 'bg-soft',
            subtle: 'bg-subtle',
            caution: 'bg-negative-strong',
            background: 'bg-base',
            inverse: 'bg-inverse',
            raised: 'bg-raised',
        };
        // Define hover classes. !important is needed here otherwise the bg is not overridden in dark mode
        const hoverClasses = {
            quiet: 'md:hover:!bg-soft',
            subtle: 'md:hover:!bg-subtle',
        };
        const effectiveHover = interactive ? resolveInteractiveHover(bg) : undefined;
        // Define transition class for hover effects
        const transitionClass = effectiveHover ? 'transition duration-normal' : '';
        return classNames(className, (shadowLayer === 'overlay' || shadowLayer === 'both') && '[:where(&)]:relative', !noBorder && boxBorderClass, transitionClass, getBoxShadowClasses({
            shadowSize,
            hoverShadow,
            shadowFocused,
            focusShadowOnPrimaryAction,
            shadowLayer,
        }), getBoxShadowOverlayClasses({
            shadowSize,
            hoverShadow,
            shadowFocused,
            focusShadowOnPrimaryAction,
            shadowLayer,
        }), bg ? variantClasses[bg] : '', effectiveHover ? hoverClasses[effectiveHover] || '' : '', resolveBoxRoundedClasses(rounded), resolveBoxPaddingClasses(padding), resolveBoxMarginClasses(margin), getBoxDisplayClassNames({ display, direction, align, justify, gap, wrap }));
    }, [
        shadowSize,
        hoverShadow,
        shadowFocused,
        focusShadowOnPrimaryAction,
        shadowLayer,
        interactive,
        className,
        bg,
        noBorder,
        rounded,
        padding,
        margin,
        display,
        direction,
        align,
        justify,
        gap,
        wrap,
    ]);
    return React.createElement(as, {
        id,
        ref,
        className: boxClass,
        onClick,
        onMouseOver,
        onMouseLeave,
        style,
        'data-testid': testId,
        ...rest,
    }, children);
}, areBoxPropsEqual);
//# sourceMappingURL=index.js.map